// İSG uzmanı portalı — Panel "Bugünkü girişler" (S34-4, design review
// `S34-carbon-phase2-plan.md` §4 "Today's entries"). Veri kaynağı `useSecurityDayEntries`
// (`result=all`) — `pages/security/DayListTab.tsx`'İN AYNI hook'u (§29.5 sayım kuralı: sayaçlar
// hook'tan HAZIR gelir, burada YENİDEN SAYILMAZ). Yalnız EN YENİ 8 satır (hook zaten `at`
// azalan döner) — `Sebep`/`Yöntem`/detay sütunları YOK (o yoğunluk `DayListTab`'e özeldir);
// satırlar `EntryDetailModal`'ı AÇMAZ, ad hücresi `/security`'e bir `TableLink`tir (design
// review "They do not open EntryDetailModal from the dashboard").
//
// **`Table` primitifi (paket) YERİNE hand-rolled `<table>`** — `DayListTab.tsx`'in AYNI
// gerekçesi: bu tablo ZATEN kendi `PanelHeader` kabuğunun İÇİNDE (bkz. `DashboardPage.tsx`),
// `Table`'ın kendi `rounded-lg border` dış kutusu paneli İKİNCİ bir kenarlıkla ÇİFTLERdi.
// `TableHeader`/`TableBody`/`TableRow`/`TableHead`/`TableCell`/`TableLink`/`TableMessageRow`/
// `TableSkeletonRows` yine de PAYLAŞILAN primitiflerdir — yalnız dış `<div>` sarmalayıcısı
// atlanır, `--row-h` (density="sm" → 32px) burada KENDİ `style`iyle tanımlanır.
//
// **S34-45 design review S2 — "Tüm sahalar" panel GÖVDESİNİ tamamen KALDIRMAZ.** Eskiden
// `DashboardPage.tsx` `activeSite` yokken bu bileşeni HİÇ monte ETMİYORDU — kullanıcıya saha
// seçicinin bu paneli AÇTIĞINI söyleyen hiçbir şey yoktu. `siteName` artık `string | null`
// (`null` = "Tüm sahalar" seçili); panel KABUĞU (başlık + `PanelHeader`) HER ZAMAN render edilir,
// yalnız gövde bir `EmptyState variant="panel"` uyarısına döner — tabloya/`entries`e HİÇ
// dokunmaz (`useSecurityDayEntries(null)` zaten `enabled:false`, o durumda `isPending` sonsuza
// dek `true` kalır; bu yüzden `siteName == null` dalı `entries`i hiç OKUMAZ).

import {
  EmptyState,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  Tag,
  currentCulture,
} from '@isgportal/shared';
import { CircleCheck, OctagonX, Users } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../lib/format';
import type { SecurityDayEntriesResult } from '../../lib/useSecurityDayEntries';
import { PanelHeader } from '../PanelHeader';

const MAX_ROWS = 8;

export function TodayEntriesPanel({
  entries,
  siteName,
}: {
  entries: SecurityDayEntriesResult;
  /** `null` — "Tüm sahalar" seçili (S2); panel kabuğu kalır, gövde saha-seç uyarısına döner. */
  siteName: string | null;
}) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const rows = entries.rows.slice(0, MAX_ROWS);
  const deniedText = entries.truncated ? '200+' : String(entries.deniedCount);

  return (
    <section
      className="@container flex flex-col rounded-sm border border-border bg-surface shadow-sm"
      aria-label={t('dashboard.entries.title')}
    >
      <PanelHeader
        icon={Users}
        title={
          siteName ? `${t('dashboard.entries.title')} — ${siteName}` : t('dashboard.entries.title')
        }
        right={
          siteName && !entries.isPending && !entries.error ? (
            <span className="tnum text-xs text-muted">
              {t('dashboard.entries.summary', {
                allowed: entries.allowedCount,
                denied: deniedText,
              })}
            </span>
          ) : null
        }
      />

      {siteName == null ? (
        <div className="px-5 py-8">
          {/* S36 #5 — üstteki KPI karosu (`entriesToday`) firma genelinde SAYAR, bu panel ise
              tek sahaya özel bir uca (`GET /security/entries`, `siteUuid` zorunlu) bağlıdır —
              açıklama olmadan "0 gösteren karo" + "seç" diyen panel bir adım atlanmış gibi
              okunurdu (S36 triage §5). İkinci satır NEDENİ söyler, akışı DEĞİŞTİRMEZ. */}
          <EmptyState
            variant="panel"
            title={t('dashboard.entries.selectSite')}
            description={t('dashboard.entries.selectSite.body')}
          />
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ '--row-h': '32px' } as CSSProperties}>
          <table className="w-full table-fixed border-collapse text-sm">
            <caption className="sr-only">{t('dashboard.entries.title')}</caption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">{t('dashboard.entries.col.time')}</TableHead>
                <TableHead>{t('dashboard.entries.col.name')}</TableHead>
                <TableHead className="hidden w-56 @[45rem]:table-cell">
                  {t('dashboard.entries.col.contractor')}
                </TableHead>
                <TableHead className="w-36">{t('dashboard.entries.col.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={entries.isPending || undefined}>
              {entries.isPending ? (
                <TableSkeletonRows columns={4} />
              ) : entries.error ? (
                <TableMessageRow colSpan={4}>
                  <div className="flex flex-col items-center gap-2">
                    <span>{t('security.day.error')}</span>
                    <button
                      type="button"
                      onClick={entries.refetch}
                      className="rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('security.gate.retry')}
                    </button>
                  </div>
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={4}>
                  <div className="flex flex-col gap-1">
                    <span>{t('dashboard.entries.empty')}</span>
                    <span className="text-xs">{t('dashboard.entries.empty.body')}</span>
                  </div>
                </TableMessageRow>
              ) : (
                rows.map((row) => {
                  const isAllowed = row.result === 'Allowed';
                  return (
                    <TableRow key={row.uuid}>
                      <TableCell className="tnum text-muted">
                        {formatTime(row.at, culture)}
                      </TableCell>
                      <TableCell>
                        <TableLink to="/security" title={row.personnelName}>
                          {row.personnelName}
                        </TableLink>
                      </TableCell>
                      <TableCell className="hidden text-muted @[45rem]:table-cell">
                        {row.contractorName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Tag color={isAllowed ? 'green' : 'red'}>
                          {isAllowed ? (
                            <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          ) : (
                            <OctagonX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          )}
                          {isAllowed
                            ? t('security.day.status.allowed')
                            : t('security.day.status.denied')}
                        </Tag>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </table>
        </div>
      )}
    </section>
  );
}
